import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BellRing, CheckCircle2, ExternalLink, Loader2, Mail, MessageSquare,
  Phone, PlayCircle, Send, ShieldCheck, Wrench, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  STAGES, ageSince, fmtTime, money, outstanding, quoteLabel,
  stageOf, vehicleLabel, workLabel,
} from "./advisorUtils";

const Stat = ({ label, value, tone = "text-slate-100" }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</div>
    <div className={`text-lg font-bold truncate ${tone}`}>{value}</div>
  </div>
);

const ActionButton = ({ icon: Icon, label, onClick, busy, tone = "slate", testid, href }) => {
  const tones = {
    slate: "bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-700",
    green: "bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-500",
    amber: "bg-amber-500 hover:bg-amber-400 text-slate-950 border-amber-400",
    sky: "bg-sky-600 hover:bg-sky-500 text-white border-sky-500",
  };
  const cls = `min-h-[60px] px-4 rounded-xl border font-bold text-base inline-flex items-center justify-center gap-2.5 transition-colors duration-150 disabled:opacity-40 ${tones[tone]}`;
  if (href) {
    return (
      <a href={href} className={`${cls} no-underline`} data-testid={testid}>
        <Icon className="w-5 h-5" /> {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={busy} className={cls} data-testid={testid}>
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />} {label}
    </button>
  );
};

export const AdvisorDetailPanel = ({ job, onChanged, onShowCustomer, onClose }) => {
  const [busy, setBusy] = useState(null);
  const [composing, setComposing] = useState(false);
  const [message, setMessage] = useState("");

  if (!job) {
    return (
      <div className="h-full flex items-center justify-center text-center p-10" data-testid="advisor-detail-empty">
        <div>
          <Wrench className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="text-lg font-bold text-slate-400">Tap a car to see everything about it</div>
          <div className="text-sm text-slate-600 mt-1">Customer, work, quote, plan and one-tap actions.</div>
        </div>
      </div>
    );
  }

  const stage = STAGES[stageOf(job)];
  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(okMsg);
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't go through — try again");
    } finally {
      setBusy(null);
    }
  };

  const sendMessage = async () => {
    const text = message.trim();
    if (!text) return;
    setBusy("text");
    try {
      const r = await api.post(`/jobs/${job.id}/messages/reply`, { text, channel: "sms" });
      setMessage("");
      setComposing(false);
      if (r?.data?.delivery === "failed") {
        toast.warning("Saved to the thread, but it didn't reach them", {
          description: r.data.delivery_error || "Check their number, or give them a call.",
        });
      } else {
        toast.success("Text sent to the customer");
      }
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't go through — try again");
    } finally {
      setBusy(null);
    }
  };

  const items = job.items || [];
  const declined = items.filter((i) => i.status === "declined").length;
  const waitingFor = ageSince(job.viewed_at || job.sent_at);

  return (
    <div className="h-full flex flex-col" data-testid="advisor-detail-panel">
      <div className="px-5 py-4 border-b border-slate-800 flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono font-black text-3xl tracking-wider" data-testid="advisor-detail-reg">
              {job.vehicle_reg || "NO REG"}
            </span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800 text-sm font-bold ring-1 ${stage.ring} ${stage.text}`} data-testid="advisor-detail-stage">
              <span className={`w-2 h-2 rounded-full ${stage.dot}`} /> {stage.label}
            </span>
          </div>
          <div className="text-lg font-semibold text-slate-200 mt-1">
            {job.customer_name || "Customer"} <span className="text-slate-500 font-normal">· {vehicleLabel(job)}</span>
          </div>
          <div className="text-sm text-slate-400">{workLabel(job)}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link to={`/jobs/${job.id}`} className="p-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 no-underline" title="Open full job" data-testid="advisor-open-job-btn">
            <ExternalLink className="w-5 h-5" />
          </Link>
          <button type="button" onClick={onClose} className="lg:hidden p-2.5 rounded-lg bg-slate-800 text-slate-300" data-testid="advisor-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-5 py-4 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="advisor-detail-stats">
          <Stat label="Promised out" value={job.promised_ready_time || "—"} />
          <Stat label="Quote" value={quoteLabel(job)} tone={job.status === "approved" ? "text-emerald-300" : "text-slate-100"} />
          <Stat label="Approved" value={money(job.approved_total)} tone="text-emerald-300" />
          <Stat label="Still out" value={money(outstanding(job))} tone={outstanding(job) > 0 ? "text-amber-300" : "text-slate-500"} />
        </div>

        {waitingFor && ["sent", "viewed", "partial"].includes(job.status) && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-200 font-semibold" data-testid="advisor-detail-waiting">
            Customer has had this {waitingFor} without deciding{declined ? ` · ${declined} declined` : ""}.
          </div>
        )}

        {job.service_plan_opportunity && (
          <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-3 flex items-center gap-2.5 text-sky-200 font-semibold" data-testid="advisor-detail-plan-opportunity">
            <ShieldCheck className="w-5 h-5 shrink-0" /> No service plan on this car — worth a conversation at handover.
          </div>
        )}

        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Work on this car</div>
          <div className="space-y-2" data-testid="advisor-detail-items">
            {items.length === 0 && <div className="text-slate-500 text-sm">Nothing added yet.</div>}
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-900/70 border border-slate-800">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  it.urgency === "red" ? "bg-red-500" : it.urgency === "amber" ? "bg-amber-400" : "bg-emerald-500"
                }`} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-100 truncate">{it.name}</div>
                  <div className="text-xs text-slate-500 capitalize">{it.status || "pending"}</div>
                </div>
                <div className="font-mono font-bold text-slate-200 tabular-nums shrink-0">
                  {money((it.unit_price || 0) * (it.quantity || 1))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {(job.customer_messages || []).length > 0 && (
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Messages</div>
            <div className="space-y-2" data-testid="advisor-detail-messages">
              {job.customer_messages.slice(-4).map((m) => (
                <div key={m.id} className={`px-3 py-2 rounded-lg text-sm ${
                  m.from === "workshop" ? "bg-emerald-500/10 text-emerald-100" : "bg-slate-800 text-slate-200"
                }`}>
                  <div>{m.text}</div>
                  <div className="text-[11px] mt-1 flex items-center gap-2">
                    <span className="text-slate-500">{fmtTime(m.created_at)}</span>
                    {m.ai && (
                      <span className="text-emerald-300 font-bold" data-testid={`advisor-msg-ai-${m.id}`}>AI assistant</span>
                    )}
                    {m.delivery === "failed" && (
                      <span className="text-amber-300 font-bold">didn&apos;t reach them</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {composing && (
        <div className="px-5 py-4 border-t border-slate-800 bg-slate-900/80" data-testid="advisor-compose-box">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder={`Text ${job.customer_name || "the customer"}…`}
            className="w-full rounded-xl bg-slate-950 border border-slate-700 text-slate-100 p-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="advisor-compose-input"
          />
          <div className="flex gap-3 mt-3">
            <ActionButton icon={Send} label="Send text" tone="green" onClick={sendMessage} busy={busy === "text"} testid="advisor-compose-send" />
            <ActionButton icon={X} label="Cancel" onClick={() => { setComposing(false); setMessage(""); }} testid="advisor-compose-cancel" />
          </div>
        </div>
      )}

      <div className="px-5 py-4 border-t border-slate-800 bg-slate-900/60 grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="advisor-actions">
        {job.customer_phone && (
          <ActionButton icon={Phone} label="Call" href={`tel:${job.customer_phone}`} testid="advisor-action-call" />
        )}
        <ActionButton icon={MessageSquare} label="Text" onClick={() => setComposing(true)} tone="sky" testid="advisor-action-text" />
        {["sent", "viewed", "partial"].includes(job.status) && (
          <ActionButton
            icon={BellRing} label="Chase quote" tone="amber"
            busy={busy === "chase"}
            onClick={() => run("chase", () => api.post(`/jobs/${job.id}/resend`, {}), "Quote re-send queued — 5 min to cancel")}
            testid="advisor-action-chase"
          />
        )}
        {job.status === "draft" && (
          <ActionButton
            icon={Mail} label="Send quote" tone="green"
            busy={busy === "send"}
            onClick={() => run("send", () => api.post(`/jobs/${job.id}/send`, {}), "Quote sent to the customer")}
            testid="advisor-action-send"
          />
        )}
        {!job.in_bay_at && (
          <ActionButton
            icon={Wrench} label="Start in bay"
            busy={busy === "bay"}
            onClick={() => run("bay", () => api.post(`/jobs/${job.id}/start-bay`, {}), "Car is on a bay")}
            testid="advisor-action-start-bay"
          />
        )}
        {job.in_bay_at && !job.workshop_complete_at && (
          <ActionButton
            icon={CheckCircle2} label="Tech done"
            busy={busy === "complete"}
            onClick={() => run("complete", () => api.post(`/jobs/${job.id}/workshop-complete`, {}), "Marked workshop complete")}
            testid="advisor-action-workshop-complete"
          />
        )}
        {!job.ready_for_collection_at && job.status !== "draft" && (
          <ActionButton
            icon={CheckCircle2} label="Mark ready" tone="green"
            busy={busy === "ready"}
            onClick={() => run("ready", () => api.post(`/jobs/${job.id}/mark-ready`, {}), "Customer told the car is ready")}
            testid="advisor-action-mark-ready"
          />
        )}
        {job.ready_for_collection_at && !job.completed_at && (
          <ActionButton
            icon={CheckCircle2} label="Collected"
            busy={busy === "collect"}
            onClick={() => run("collect", () => api.post(`/jobs/${job.id}/collect`, {}), "Handover recorded")}
            testid="advisor-action-collect"
          />
        )}
        <ActionButton icon={PlayCircle} label="Show customer" tone="sky" onClick={() => onShowCustomer(job)} testid="advisor-action-show-customer" />
      </div>
    </div>
  );
};
